// 投票維護整頁（vote-vote-maintenance＝legacy vote/voteMaintenance.jsp）。
// 批次 17（B8-3，Gitea issue #16）Wave 1。掛在 PageBehavior.complexBody（批次 13/16 hook 先例）。
//
// legacy 流程（行號全部親驗 2026-09-25，基準＝ePortfolio_work/.../views/vote＋js-my/vote/voteMaintenance.js；
// 詳 docs/plans/2026-09-25-batch17-vote-setting-plan.md §1.1/§1.2/§1.4）：
//   - ready（js:17-25）＝綁定＋init 表單/表格＋**query()（ready 即查詢）**。
//   - 查詢表單（jsp:50-124）：新增 #add（UPDATE gate）／*年度 annualId（全部 -1）／*醫院 organizationId／
//     投票期間 voteStartDate/voteEndDate＋resetTime／資料篩選期間 dataStartDate/dataEndDate＋resetTime／
//     查詢 #query＋匯出 #exportVoteResult（EXPORT gate）。
//   - 匯入醫師名單區（jsp:126-146，EXPORT gate）：h4＋importFile＋importTarget（投票名稱下拉）＋匯入
//     #importTeacher＋使用說明＋範本下載 #downloadTeacherTmpl（**vote/downlad typo 照實**）＋ol li×4 逐字。
//   - 主表 8 欄（js:317-366）：col0 刪除鈕（enable>0＋DELETE）／已停用（0）／已刪除（-1）；col1 年度（民國
//     annualDisp=0）；col2 投票名稱 a.edit（UPDATE）；col3 投票期間；col4 資料篩選期間；col5/col6 檢視
//     （醫師名單 teacherList／投票結果 voteResult）；col7 停用（1）／啟用（0）。
//   - 流程（js:30-162）：存檔→valid→sysConfirm 確定要儲存嗎?（:40）；啟停/刪除→sysConfirm
//     確定要{停用|刪除|啟用}投票[name]嗎?；匯入→請選擇匯入檔案!(:123)→請選擇匯入的投票!(:127)→
//     sysConfirm 確定要匯入嗎?(:131)→xlsx 檢查→僅能上傳xlsx檔案！(:138)；年度/醫院 change→setImportTarget。
//   - save（js:418-446）：POST vote/save→成功 query＋info(desc)＋hide modal；error→儲存失敗，請聯絡系統管理員！
//     getFormData（:451-482）：endDate/dataEndDate→23:59:59；新投票帶 enable=ENABLE＋category=EXCELLENT_TEACHER。
//   - modal（formModal.jsp）：#voteFormModal（新增投票/修改投票）；欄位 id/annualId（data-default 預設）/
//     organizationId/name(maxlength100)/startDate/endDate/dataStartDate/dataEndDate＋footer 關閉/儲存。
//   - 檢視 modal：excllentTaecherModal（優良臨床教師名單 4 欄）；voteResultModal（title={name}-投票結果，7 欄）。
//
// 雛形策略（執行文件 D 系列）：查詢＝dataSource.list（endpoint-map list:vote-vote-maintenance ✓）＋
// dataEffect（C5-j）；filters 以契約 DOM name 直送（legacy JS 內部另轉 orgId/startDate 參數名，mock 層不需要，
// 登記）；刪除＝remove（remove:… ✓ /vote/delete）；存檔＝create/update、停用/啟用＝update toggle（endpoint-map
// 無 vote/save|disable|enable 條目→parity 未映射，登記）；匯入＝0 data-effect＋雛形成功訊息（批次 9 先例）；
// 範本＝Blob 下載（批次 9 downloadTemplateBlob 先例）；匯出＝list export:true（批次 14 先例，
// dataEffect=list:…#export）；醫師名單/投票結果 modal＝樣本常數（0 data-effect）。
//
// legacy quirk 照實（D6）：①openFormModal 新增時 select[name=orgId] 撈不到（JSP 是 organizationId）＝
// org 無預設 ②getVoteQueryData select[name=voteId] 不存在＝不帶 ③匯入副檔名不符時 $file.val() 無效呼叫
// （不清檔）④downlad typo ⑤endDate 23:59:59：legacy 前端 setUTCHours 有時區偏移 quirk，雛形以
// `date + ' 23:59:59'` 直送（server MyDateUtils.getThisDayLastTime 同語意）⑥resetTime 圓鈕以文字鈕呈現。
//
// 【證據等級】§7 第 3 級（原始碼）。

import { useEffect, useState, type ReactElement } from 'react';
import { panelHeadingLevelFor } from '@/app/panel-heading-levels';
import { Button } from '@/components/base/Button';
import { Select } from '@/components/base/Select';
import { Table, type TableRow } from '@/components/base/Table';
import { TextInput } from '@/components/base/TextInput';
import { ContentPanel, ModalFrame, ProcessingOverlay, useDialogs } from '@/components/layouts';
import { LegacyContractModals } from '@/components/layouts/LegacyContractModals';
import { t } from '@/messages/zh-TW';
import { MockDataError, useDataSource } from '@/mocks';
import { selectOptionsOverride } from '@/mocks/legacy-fields';
import {
  ACCOUNT_ORGANIZATION_OPTIONS,
  COURSE_CLAZZ_ANNUAL_OPTIONS,
} from '@/mocks/resource-rules';
import type { Row } from '@/mocks/types';

/** 查詢年度選項（js:56-66：`全部` -1＋年度池；值＝民國年【雛形值待實機校準】）。 */
const VOTE_ANNUAL_QUERY_OPTIONS = [
  { value: '-1', label: '全部' },
  ...COURSE_CLAZZ_ANNUAL_OPTIONS,
];

/** 訊息字面值（js／VoteController.java；!＝0021、?＝003F、！＝FF01）。 */
const TEXT_SAVE_CONFIRM = '確定要儲存嗎?'; // js:34（legacy.unique.32）
const TEXT_IMPORT_CONFIRM = '確定要匯入嗎?'; // js:131
const TEXT_SAVE_SUCCESS = '儲存成功'; // VoteController.java:280
const TEXT_DISABLE_SUCCESS = '停用成功'; // :519
const TEXT_ENABLE_SUCCESS = '啟用成功'; // :543
const TEXT_DELETE_SUCCESS = '刪除成功'; // :492
const TEXT_IMPORT_SUCCESS = '匯入成功'; // :236
const TEXT_SAVE_ERROR = '儲存失敗，請聯絡系統管理員！'; // js:461（legacy.unique.09）
const TEXT_DISABLE_ERROR = '停用失敗，請聯絡系統管理員！'; // js:596（legacy.unique.07）
const TEXT_ENABLE_ERROR = '啟用失敗，請聯絡系統管理員！'; // js:646（legacy.unique.13）
const TEXT_DELETE_ERROR = '刪除失敗，請聯絡系統管理員！'; // js:621（legacy.unique.10）
const TEXT_QUERY_ERROR = '查詢失敗，請聯絡系統管理員！'; // js:529/:600/:632/:666
// 匯入失敗！（js:197）＝legacy ajax catch 路徑；雛形匯入無 ajax（0 data-effect）不觸發，不鏡像。
// 下載失敗，請聯絡系統管理員！（js:113）＝legacy fileDownload failCallback；雛形 Blob 同步下載無 fail 分支。
const TEXT_NO_FILE = '請選擇匯入檔案!'; // js:123
const TEXT_NO_VOTE = '請選擇匯入的投票!'; // js:127（legacy.unique.46）
const TEXT_ONLY_XLSX = '僅能上傳xlsx檔案！'; // js:138（！＝FF01）
const TEXT_EXPORT_FAIL = '下載檔案失敗！'; // js:671（legacy.global.01 同文）

/** 醫師名單 modal 樣本（getTeacherList；0 data-effect placeholder【雛形值待實機校準】）。 */
const TEACHER_LIST_SAMPLE: readonly Row[] = [
  { teacherCategory: '主治醫師', name: '教師甲', countryId: 'A123456789', departmentName: '內科部' },
  { teacherCategory: '住院醫師', name: '醫師乙', countryId: 'B198765432', departmentName: '外科部' },
];

/** 投票結果 modal 樣本（getVoteResult 7 欄；0 data-effect placeholder【雛形值待實機校準】）。 */
const VOTE_RESULT_SAMPLE: readonly Row[] = [
  {
    category: '主治醫師',
    deptName: '內科部',
    accountName: '教師甲',
    studentVotedCount: 12,
    studentTotalVoteCount: 30,
    studentVoteScore: '40.0%',
    deptManagerRecommend: '是',
  },
];

/** 投票表單 modal 狀態（legacy #voteForm #voteFormModal）。 */
interface VoteFormState {
  id: string;
  annualId: string;
  orgId: string;
  name: string;
  startDate: string;
  endDate: string;
  dataStartDate: string;
  dataEndDate: string;
  invalid: ReadonlySet<string>;
}

/** enable 三態正規化（legacy 列 enable.id 1/0/-1；表單送出值 'ENABLE'/'DISABLE'，js:479-480）。 */
function enableIdOf(row: Row): number {
  const raw = row['enable'];
  if (raw === 'ENABLE') {
    return 1;
  }
  if (raw === 'DISABLE') {
    return 0;
  }
  const num = Number(raw ?? 0);
  return Number.isNaN(num) ? 0 : num;
}

/** 匯入醫師名單的投票下拉（setImportTarget js:219-237：ENABLE 列，`{name}({dataDuration})`；空→`無` 0）。 */
function importTargetOptions(votes: readonly Row[]): readonly { value: string; label: string }[] {
  const enabled = votes.filter((row) => enableIdOf(row) === 1);
  if (enabled.length === 0) {
    return [{ value: '0', label: '無' }];
  }
  return enabled.map((row) => ({
    value: String(row['id']),
    label: `${String(row['name'] ?? '')}(${String(row['dataDuration'] ?? '')})`,
  }));
}

export function VoteMaintenanceBody({ pageId }: { pageId: string }): ReactElement {
  const dataSource = useDataSource();
  const dialogs = useDialogs();

  // ------------------------------------------------------------------
  // 查詢表單（jsp:50-124）。filters 以契約 DOM name 直送（C5-h；D4）。
  // ------------------------------------------------------------------
  const [annualId, setAnnualId] = useState('-1');
  const [organizationId, setOrganizationId] = useState('org-1');
  const [voteStartDate, setVoteStartDate] = useState('');
  const [voteEndDate, setVoteEndDate] = useState('');
  const [dataStartDate, setDataStartDate] = useState('');
  const [dataEndDate, setDataEndDate] = useState('');
  const [busy, setBusy] = useState(false);

  /** 查詢條件（getVoteQueryData js:487-511 鏡像：orgId 恆帶；annualId>0 才帶；日期非空才帶）。 */
  const queryFilters = (): Record<string, string> => ({
    organizationId,
    ...(parseInt(annualId, 10) > 0 ? { annualId } : {}),
    ...(voteStartDate !== '' ? { voteStartDate } : {}),
    ...(voteEndDate !== '' ? { voteEndDate } : {}),
    ...(dataStartDate !== '' ? { dataStartDate } : {}),
    ...(dataEndDate !== '' ? { dataEndDate } : {}),
  });

  // ------------------------------------------------------------------
  // 資料（ready 即查詢 js:25；setImportTarget js:219-237）
  // ------------------------------------------------------------------
  const [votes, setVotes] = useState<readonly Row[]>([]);
  const [loading, setLoading] = useState(true);

  const query = () => {
    void (async () => {
      setBusy(true);
      try {
        // GET vote/list（js:516-537）；錯誤 scenario 走 alert（legacy error→查詢失敗，請聯絡系統管理員！）。
        const result = await dataSource.list(pageId, { filters: queryFilters() });
        setVotes(result.rows);
        // setImportTarget（js:219-237）：ENABLE 列。
        const enabled = await dataSource.list(pageId, {
          filters: {
            organizationId,
            ...(parseInt(annualId, 10) > 0 ? { annualId } : {}),
          },
        });
        setImportVotes(enabled.rows.filter((row) => enableIdOf(row) === 1));
      } catch (cause: unknown) {
        await dialogs.alert(cause instanceof MockDataError ? cause.message : TEXT_QUERY_ERROR);
      } finally {
        setBusy(false);
        setLoading(false);
      }
    })();
  };

  const [importVotes, setImportVotes] = useState<readonly Row[]>([]);

  useEffect(() => {
    query(); // ready 即查詢（js:25）
    // pageId 一次掛載內不變（ComplexFormPage 同款註記）。
    // biome-ignore lint/correctness/useExhaustiveDependencies: 見上
  }, [pageId]);

  // ------------------------------------------------------------------
  // 表單 modal（openFormModal js:251-271／setVoteForm js:276-288／save js:418-482）
  // ------------------------------------------------------------------
  const [formModal, setFormModal] = useState<VoteFormState | null>(null);

  /** 新增（openFormModal()）：title 新增投票；annual 預設 data-default=1（雛形='115'）；**org 無預設
   *（legacy select[name=orgId] 撈不到＝no-op quirk 照實，D6①）**；inputs 清空。 */
  const openNewVote = () => {
    setFormModal({
      id: '',
      annualId: '115',
      orgId: '',
      name: '',
      startDate: '',
      endDate: '',
      dataStartDate: '',
      dataEndDate: '',
      invalid: new Set(),
    });
  };

  /** 修改（a.edit→openFormModal(row)）：setVoteForm 回填（voteDuration/dataDuration split('~')；
   * legacy datepicker 格式 yyyy/MM/dd→雛形原生 type=date 轉 ISO yyyy-MM-dd【雛形值待實機校準】）。 */
  const openEditVote = (row: Row) => {
    const toIso = (value: string): string => value.replace(/\//g, '-');
    const voteDuration = String(row['voteDuration'] ?? '').split('~');
    const dataDuration = String(row['dataDuration'] ?? '').split('~');
    setFormModal({
      id: String(row['id'] ?? ''),
      annualId: String(row['annualId'] ?? ''),
      orgId: String(row['organizationId'] ?? ''),
      name: String(row['name'] ?? ''),
      startDate: toIso(voteDuration[0] ?? ''),
      endDate: toIso(voteDuration[1] ?? ''),
      dataStartDate: toIso(dataDuration[0] ?? ''),
      dataEndDate: toIso(dataDuration[1] ?? ''),
      invalid: new Set(),
    });
  };

  /** 儲存（#save js:36-46→save js:418-446）：required→sysConfirm→create/update。 */
  const saveVote = () => {
    void (async () => {
      if (formModal === null) {
        return;
      }
      // $form.valid()（js:38）：required＝醫院/投票名稱/四日期（annual 無 required 屬性照實）；
      // inline 錯誤以 invalid 樣式鏡像（B1-2 先例），不過就不送。
      const invalid = new Set<string>();
      if (formModal.orgId === '') {
        invalid.add('orgId');
      }
      if (formModal.name.trim() === '') {
        invalid.add('name');
      }
      for (const key of ['startDate', 'endDate', 'dataStartDate', 'dataEndDate'] as const) {
        if (formModal[key] === '') {
          invalid.add(key);
        }
      }
      setFormModal({ ...formModal, invalid });
      if (invalid.size > 0) {
        return;
      }
      const confirmed = await dialogs.confirm(TEXT_SAVE_CONFIRM); // js:40
      if (!confirmed) {
        return;
      }
      const isNew = formModal.id === '';
      // getFormData（js:451-482）：endDate/dataEndDate→當天 23:59:59（D6⑤）；新投票帶 enable/category。
      const values: Row = {
        orgId: formModal.orgId,
        annualId: formModal.annualId,
        name: formModal.name,
        startDate: formModal.startDate,
        endDate: `${formModal.endDate} 23:59:59`,
        dataStartDate: formModal.dataStartDate,
        dataEndDate: `${formModal.dataEndDate} 23:59:59`,
        ...(isNew ? { enable: 'ENABLE', category: 'EXCELLENT_TEACHER' } : { id: formModal.id }),
      };
      setBusy(true);
      try {
        if (isNew) {
          await dialogs.processing.wrap(dataSource.create(pageId, values));
        } else {
          await dialogs.processing.wrap(dataSource.update(pageId, formModal.id, values));
        }
        setFormModal(null); // legacy 順序 query→info→hide（js:427-431）；React 先關再 info（B1-2 同款理由）
        await dialogs.info(TEXT_SAVE_SUCCESS);
        query();
      } catch (cause: unknown) {
        await dialogs.alert(cause instanceof MockDataError ? cause.message : TEXT_SAVE_ERROR);
      } finally {
        setBusy(false);
      }
    })();
  };

  // ------------------------------------------------------------------
  // 啟停/刪除（js:60-82→disableVote/deleteVote/enableVote js:542-583）
  // ------------------------------------------------------------------
  const toggleVote = (row: Row, nextEnable: number, successText: string, errorText: string) => {
    void (async () => {
      const action = nextEnable === 0 ? '停用' : '啟用';
      const confirmed = await dialogs.confirm(`確定要${action}投票[${String(row['name'] ?? '')}]嗎?`);
      if (!confirmed) {
        return;
      }
      setBusy(true);
      try {
        await dialogs.processing.wrap(
          dataSource.update(pageId, String(row['id']), { enable: nextEnable }),
        );
        await dialogs.info(successText);
        query();
      } catch (cause: unknown) {
        await dialogs.alert(cause instanceof MockDataError ? cause.message : errorText);
      } finally {
        setBusy(false);
      }
    })();
  };

  const deleteVote = (row: Row) => {
    void (async () => {
      const confirmed = await dialogs.confirm(`確定要刪除投票[${String(row['name'] ?? '')}]嗎?`);
      if (!confirmed) {
        return;
      }
      setBusy(true);
      try {
        await dialogs.processing.wrap(dataSource.remove(pageId, String(row['id'])));
        await dialogs.info(TEXT_DELETE_SUCCESS);
        query();
      } catch (cause: unknown) {
        await dialogs.alert(cause instanceof MockDataError ? cause.message : TEXT_DELETE_ERROR);
      } finally {
        setBusy(false);
      }
    })();
  };

  // ------------------------------------------------------------------
  // 檢視 modal（js:84-95 voteResult／:155-162 teacherList；0 data-effect）
  // ------------------------------------------------------------------
  const [teacherModalOpen, setTeacherModalOpen] = useState(false);
  const [resultModal, setResultModal] = useState<null | { title: string }>(null);

  // ------------------------------------------------------------------
  // 匯入醫師名單（js:118-142＋importTeacherFile js:167-200；0 data-effect，批次 9 先例）
  // ------------------------------------------------------------------
  const [importFileName, setImportFileName] = useState('');
  const [importFileEpoch, setImportFileEpoch] = useState(0); // 成功後清檔＝remount（legacy $("#importFile").val("")）
  const [importVoteId, setImportVoteId] = useState('');

  const importTeachers = () => {
    void (async () => {
      // js:122-126：無檔→請選擇匯入檔案!；voteId 無/0→請選擇匯入的投票!。
      if (importFileName === '') {
        await dialogs.alert(TEXT_NO_FILE);
        return;
      }
      if (importVoteId === '' || importVoteId === '0') {
        await dialogs.alert(TEXT_NO_VOTE);
        return;
      }
      const confirmed = await dialogs.confirm(TEXT_IMPORT_CONFIRM); // js:131
      if (!confirmed) {
        return;
      }
      const extension = importFileName.replace(/^.*\./, '');
      if (extension !== 'xlsx') {
        // js:138：僅能上傳xlsx檔案！；**$file.val() 無效呼叫＝不清檔（D6③）**。
        await dialogs.alert(TEXT_ONLY_XLSX);
        return;
      }
      // importTeacherFile（js:167-200）：成功→清檔＋info(resp.desc)（匯入成功）；雛形不處理檔案內容。
      setImportFileEpoch((epoch) => epoch + 1);
      setImportFileName('');
      await dialogs.info(TEXT_IMPORT_SUCCESS);
    })();
  };

  /** 範本下載（js:107-116→vote/downlad/excellentTeacherTmpl；Blob 下載批次 9 先例【雛形值待實機校準】）。 */
  const downloadTemplate = () => {
    if (typeof URL.createObjectURL !== 'function') {
      return;
    }
    const blob = new Blob(
      ['React 雛形佔位範本（legacy 來源：vote/downlad/excellentTeacherTmpl（downlad typo 照實），實機校準後由真後端提供）'],
      { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' },
    );
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = '醫師名單範本.xlsx';
    anchor.click();
    URL.revokeObjectURL(url);
  };

  /** 匯出（js:144-147/:654-672→vote/report/excellentTeacher；list export:true 批次 14 先例）。 */
  const exportReport = () => {
    void (async () => {
      setBusy(true);
      try {
        await dataSource.list(pageId, {
          filters: {
            organizationId,
            ...(parseInt(annualId, 10) > 0 ? { annualId } : {}),
          },
          export: true,
        });
        await dialogs.info(t('export.notice'));
      } catch {
        await dialogs.alert(TEXT_EXPORT_FAIL);
      } finally {
        setBusy(false);
      }
    })();
  };

  // ------------------------------------------------------------------
  // 畫面 helper
  // ------------------------------------------------------------------
  const dateInput = (
    uiId: string,
    name: string | undefined,
    value: string,
    onChange: (next: string) => void,
  ) => (
    // legacy datepicker readonly（data-provide=datepicker）；雛形＝原生 type=date（批次 14/15 先例）。
    <input
      data-ui-id={uiId}
      {...(name !== undefined ? { name } : {})}
      type="date"
      className="border border-gray-300 px-2 py-1 text-sm"
      value={value}
      onChange={(event) => {
        onChange(event.target.value);
      }}
    />
  );

  const resetButton = (uiId: string, onReset: () => void) => (
    <Button uiId={uiId} variant="default" size="sm" type="button" onClick={onReset}>
      {/* jsp:94-96/:112-114 resetTime 圓鈕（eraser icon）；雛形以文字鈕呈現（D6⑥） */}
      清除
    </Button>
  );

  const rowKey = (row: Row): string => String(row['id']).toLowerCase();

  return (
    <div data-ui-id={pageId}>
      <LegacyContractModals pageId={pageId} />
      <ProcessingOverlay uiId={`${pageId}-processing`} open={loading} />
      <ContentPanel
        uiId={`${pageId}-panel`}
        title="投票維護"
        headingLevel={panelHeadingLevelFor(pageId)}
      >
        <div className="grid gap-6 md:grid-cols-2">
          {/* 查詢表單（jsp:50-124 #queryForm）。 */}
          <div data-ui-id={`${pageId}-query-form`} className="space-y-2">
            <div>
              <Button
                uiId={`${pageId}-add-btn`}
                variant="primary"
                size="sm"
                type="button"
                onClick={openNewVote}
              >
                {/* jsp:53（UPDATE gate；admin 視角全顯，D5） */}
                新增
              </Button>
            </div>
            <div className="flex items-center gap-2">
              <label className="w-32 shrink-0 text-right text-sm" htmlFor={`${pageId}-query-annual`}>
                <span className="text-danger">*</span>年度：
              </label>
              <Select
                uiId={`${pageId}-query-annual`}
                name="annualId"
                value={annualId}
                onChange={setAnnualId}
                // 批次 52 種子值校準：凍結選項集 [-1=NaN,1=115]（裁定 G，NaN 鏡像）。
                options={selectOptionsOverride(pageId, 'annualId') ?? VOTE_ANNUAL_QUERY_OPTIONS}
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="w-32 shrink-0 text-right text-sm" htmlFor={`${pageId}-query-org`}>
                <span className="text-danger">*</span>醫院：
              </label>
              <Select
                uiId={`${pageId}-query-org`}
                name="organizationId"
                value={organizationId}
                onChange={setOrganizationId}
                options={selectOptionsOverride(pageId, 'organizationId') ?? ACCOUNT_ORGANIZATION_OPTIONS}
              />
            </div>
            <div className="flex items-center gap-2">
              <span className="w-32 shrink-0 text-right text-sm">投票期間：</span>
              {dateInput(`${pageId}-query-vote-start`, 'voteStartDate', voteStartDate, setVoteStartDate)}
              {dateInput(`${pageId}-query-vote-end`, 'voteEndDate', voteEndDate, setVoteEndDate)}
              {resetButton(`${pageId}-query-vote-reset`, () => {
                setVoteStartDate('');
                setVoteEndDate('');
              })}
            </div>
            <div className="flex items-center gap-2">
              <span className="w-32 shrink-0 text-right text-sm">資料篩選期間：</span>
              {dateInput(`${pageId}-query-data-start`, 'dataStartDate', dataStartDate, setDataStartDate)}
              {dateInput(`${pageId}-query-data-end`, 'dataEndDate', dataEndDate, setDataEndDate)}
              {resetButton(`${pageId}-query-data-reset`, () => {
                setDataStartDate('');
                setDataEndDate('');
              })}
            </div>
            <div className="flex gap-2">
              <Button
                uiId={`${pageId}-query-btn`}
                variant="primary"
                size="sm"
                type="button"
                // C5-j：查詢真的呼叫 dataSource.list；C5-h 送出自報＝complexBodySubmitId。
                // 不 disabled（transcript 先例：legacy 以 overlay 擋點擊，按鈕恆可按＝測試 race 安全）。
                dataEffect={`list:${pageId}`}
                onClick={query}
              >
                {/* jsp:121 */}
                查詢
              </Button>
              <Button
                uiId={`${pageId}-export-btn`}
                variant="primary"
                size="sm"
                type="button"
                // C5-j：匯出真的呼叫 dataSource.list export（批次 14 先例）。
                dataEffect={`list:${pageId}#export`}
                onClick={exportReport}
              >
                {/* jsp:122（EXPORT gate） */}
                匯出
              </Button>
            </div>
          </div>

          {/* 匯入醫師名單區（jsp:126-146；EXPORT gate）。 */}
          <div data-ui-id={`${pageId}-import-area`} className="space-y-2">
            <h4 className="text-sm font-bold text-ink">匯入醫師名單:</h4>
            <div className="flex items-center gap-2">
              <input
                key={importFileEpoch}
                data-ui-id={`${pageId}-import-file`}
                type="file"
                className="text-sm"
                onChange={(event) => {
                  setImportFileName(event.target.files?.[0]?.name ?? '');
                }}
              />
              <Select
                uiId={`${pageId}-import-target`}
                value={importVoteId}
                onChange={setImportVoteId}
                options={importTargetOptions(importVotes)}
              />
              <Button
                uiId={`${pageId}-import-teacher-btn`}
                variant="primary"
                size="sm"
                type="button"
                onClick={importTeachers}
              >
                {/* jsp:129 */}
                匯入
              </Button>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-ink">使用說明:</span>
              <Button
                uiId={`${pageId}-download-tmpl`}
                variant="default"
                size="sm"
                type="button"
                onClick={() => {
                  downloadTemplate();
                  // js:107-116 failCallback（下載失敗，請聯絡系統管理員！）＝Blob 同步路徑無 fail 分支
                  //（與批次 9/15 範本下載同款【雛形值待實機校準】）。
                }}
              >
                範本下載
              </Button>
            </div>
            <ol className="list-decimal pl-5 text-sm text-ink">
              {/* jsp:136-141 逐字 */}
              <li>請下載範本依照欄位說明貼入內容。</li>
              <li>請依據本檔案欄位說明，維護醫師資料。</li>
              <li>檔案匯入時，請選擇要將老師名單匯入哪一次的投票名稱。</li>
              <li>再次匯入時，會把原有匯入資料清除，以本次匯入資料為主。</li>
            </ol>
          </div>
        </div>

        <hr className="my-4 border-gray-300" />

        {/* 主表（jsp:149-163 #voteListTable 8 欄；js:317-366 欄渲染）。 */}
        <Table
          uiId={`${pageId}-result-table`}
          columns={[
            { key: 'action', header: '', align: 'center' },
            { key: 'annualYear', header: '年度', align: 'center' },
            { key: 'name', header: '投票名稱', align: 'center' },
            { key: 'voteDuration', header: '投票期間', align: 'center' },
            { key: 'dataDuration', header: '資料篩選期間', align: 'center' },
            { key: 'teacher', header: '醫師名單', align: 'center' },
            { key: 'result', header: '投票結果', align: 'center' },
            { key: 'enable', header: '狀態', align: 'center' },
          ]}
          rows={votes as readonly TableRow[]}
          rowKey={rowKey}
          emptyText="查無符合的資料"
          renderCell={(row, columnKey) => {
            const enable = enableIdOf(row);
            const key = rowKey(row);
            if (columnKey === 'action') {
              // col0（js:318-329）：刪除（enable>0＋DELETE）/已停用（0）/已刪除（-1）。
              if (enable > 0) {
                return (
                  <Button
                    uiId={`${pageId}-row-${key}-delete`}
                    variant="danger"
                    size="sm"
                    type="button"
                    // C5-j：刪除真的呼叫 dataSource.remove（confirm 後；CrudListPage 同款）。
                    dataEffect={`remove:${pageId}`}
                    onClick={() => {
                      deleteVote(row);
                    }}
                  >
                    刪除
                  </Button>
                );
              }
              return enable === 0 ? '已停用' : '已刪除';
            }
            if (columnKey === 'annualYear') {
              return String(row['annualYear'] ?? ''); // 民國（annualDisp=0）
            }
            if (columnKey === 'name') {
              // col2（js:330-341）：updatePermission→a.edit 連結。
              return (
                <button
                  data-ui-id={`${pageId}-row-${key}-edit`}
                  type="button"
                  className="underline"
                  onClick={() => {
                    openEditVote(row);
                  }}
                >
                  {String(row['name'] ?? '')}
                </button>
              );
            }
            if (columnKey === 'voteDuration') {
              return String(row['voteDuration'] ?? '');
            }
            if (columnKey === 'dataDuration') {
              return String(row['dataDuration'] ?? '');
            }
            if (columnKey === 'teacher') {
              return (
                <Button
                  uiId={`${pageId}-row-${key}-teacher`}
                  variant="default"
                  size="sm"
                  type="button"
                  onClick={() => {
                    setTeacherModalOpen(true); // js:155-162（getTeacherList→render→show；樣本常數）
                  }}
                >
                  檢視
                </Button>
              );
            }
            if (columnKey === 'result') {
              return (
                <Button
                  uiId={`${pageId}-row-${key}-result`}
                  variant="default"
                  size="sm"
                  type="button"
                  onClick={() => {
                    setResultModal({
                      title: `${String(row['name'] ?? '')}-投票結果`, // js:89
                    });
                  }}
                >
                  檢視
                </Button>
              );
            }
            if (columnKey === 'enable') {
              // col7（js:352-364）：停用（1）/啟用（0）。
              if (enable === 1) {
                return (
                  <Button
                    uiId={`${pageId}-row-${key}-disable`}
                    variant="default"
                    size="sm"
                    type="button"
                    // C5-j：停用真的呼叫 dataSource.update（confirm 後；endpoint-map 無 vote/disable，登記）。
                    dataEffect={`update:${pageId}`}
                    onClick={() => {
                      toggleVote(row, 0, TEXT_DISABLE_SUCCESS, TEXT_DISABLE_ERROR);
                    }}
                  >
                    停用
                  </Button>
                );
              }
              if (enable === 0) {
                return (
                  <Button
                    uiId={`${pageId}-row-${key}-enable`}
                    variant="default"
                    size="sm"
                    type="button"
                    dataEffect={`update:${pageId}`}
                    onClick={() => {
                      toggleVote(row, 1, TEXT_ENABLE_SUCCESS, TEXT_ENABLE_ERROR);
                    }}
                  >
                    啟用
                  </Button>
                );
              }
              return '\u00a0';
            }
            return '\u00a0';
          }}
        />
      </ContentPanel>

      {/* 投票表單 modal（modal/formModal.jsp；疊放自訂 modal，B1-2 同款）。 */}
      {formModal !== null ? (
        <ModalFrame
          uiId={`${pageId}-vote-form-modal`}
          open
          title={formModal.id === '' ? '新增投票' : '修改投票'} // js:257/:254
          size="lg"
          onClose={() => {
            setFormModal(null);
          }}
          footer={
            <>
              <Button
                uiId={`${pageId}-vote-form-close`}
                variant="default"
                size="sm"
                type="button"
                onClick={() => {
                  setFormModal(null);
                }}
              >
                {/* formModal.jsp:76 */}
                關閉
              </Button>
              <Button
                uiId={`${pageId}-vote-form-save`}
                variant="primary"
                size="sm"
                type="button"
                disabled={busy}
                // C5-j：存檔真的呼叫 dataSource.create/update（endpoint-map 無 vote/save 條目，登記）。
                dataEffect={`${formModal.id === '' ? 'create' : 'update'}:${pageId}`}
                onClick={saveVote}
              >
                {/* formModal.jsp:79（#save） */}
                儲存
              </Button>
            </>
          }
        >
          <div data-ui-id={`${pageId}-vote-form-body`} className="space-y-2">
            <div className="flex items-center gap-2">
              <label className="w-32 shrink-0 text-right text-sm" htmlFor={`${pageId}-vote-form-annual`}>
                <span className="text-danger">*</span>年度：
              </label>
              <Select
                uiId={`${pageId}-vote-form-annual`}
                value={formModal.annualId}
                onChange={(next) => {
                  setFormModal({ ...formModal, annualId: next });
                }}
                options={COURSE_CLAZZ_ANNUAL_OPTIONS}
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="w-32 shrink-0 text-right text-sm" htmlFor={`${pageId}-vote-form-org`}>
                <span className="text-danger">*</span>醫院：
              </label>
              <Select
                uiId={`${pageId}-vote-form-org`}
                value={formModal.orgId}
                invalid={formModal.invalid.has('orgId')}
                onChange={(next) => {
                  setFormModal({ ...formModal, orgId: next, invalid: new Set([...formModal.invalid].filter((k) => k !== 'orgId')) });
                }}
                placeholder="請選擇"
                options={ACCOUNT_ORGANIZATION_OPTIONS}
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="w-32 shrink-0 text-right text-sm" htmlFor={`${pageId}-vote-form-name`}>
                <span className="text-danger">*</span>投票名稱：
              </label>
              <TextInput
                uiId={`${pageId}-vote-form-name`}
                value={formModal.name}
                invalid={formModal.invalid.has('name')}
                maxLength={100}
                onChange={(next) => {
                  setFormModal({ ...formModal, name: next, invalid: new Set([...formModal.invalid].filter((k) => k !== 'name')) });
                }}
              />
            </div>
            <div className="flex items-center gap-2">
              <span className="w-32 shrink-0 text-right text-sm">
                <span className="text-danger">*</span>投票期間：
              </span>
              {dateInput(
                `${pageId}-vote-form-start`,
                undefined,
                formModal.startDate,
                (next) => {
                  setFormModal({ ...formModal, startDate: next, invalid: new Set([...formModal.invalid].filter((k) => k !== 'startDate')) });
                },
              )}
              {dateInput(
                `${pageId}-vote-form-end`,
                undefined,
                formModal.endDate,
                (next) => {
                  setFormModal({ ...formModal, endDate: next, invalid: new Set([...formModal.invalid].filter((k) => k !== 'endDate')) });
                },
              )}
              {resetButton(`${pageId}-vote-form-vote-reset`, () => {
                setFormModal({ ...formModal, startDate: '', endDate: '' });
              })}
            </div>
            <div className="flex items-center gap-2">
              <span className="w-32 shrink-0 text-right text-sm">
                <span className="text-danger">*</span>資料篩選期間：
              </span>
              {dateInput(
                `${pageId}-vote-form-data-start`,
                undefined,
                formModal.dataStartDate,
                (next) => {
                  setFormModal({ ...formModal, dataStartDate: next, invalid: new Set([...formModal.invalid].filter((k) => k !== 'dataStartDate')) });
                },
              )}
              {dateInput(
                `${pageId}-vote-form-data-end`,
                undefined,
                formModal.dataEndDate,
                (next) => {
                  setFormModal({ ...formModal, dataEndDate: next, invalid: new Set([...formModal.invalid].filter((k) => k !== 'dataEndDate')) });
                },
              )}
              {resetButton(`${pageId}-vote-form-data-reset`, () => {
                setFormModal({ ...formModal, dataStartDate: '', dataEndDate: '' });
              })}
            </div>
          </div>
        </ModalFrame>
      ) : null}

      {/* 醫師名單 modal（modal/excellentTeacherList.jsp：優良臨床教師名單 4 欄）。 */}
      {teacherModalOpen ? (
        <ModalFrame
          uiId={`${pageId}-teacher-modal`}
          open
          title="優良臨床教師名單"
          size="lg"
          onClose={() => {
            setTeacherModalOpen(false);
          }}
          footer={
            <Button
              uiId={`${pageId}-teacher-modal-footer-close`}
              variant="default"
              size="sm"
              type="button"
              onClick={() => {
                setTeacherModalOpen(false);
              }}
            >
              關閉
            </Button>
          }
        >
          <Table
            uiId={`${pageId}-teacher-table`}
            columns={[
              { key: 'teacherCategory', header: '類別', align: 'center' },
              { key: 'name', header: '姓名', align: 'center' },
              { key: 'countryId', header: '身分證字號', align: 'center' },
              { key: 'departmentName', header: '科部', align: 'center' },
            ]}
            rows={TEACHER_LIST_SAMPLE as readonly TableRow[]}
            rowKey={(row) => String(row['name'] ?? '')}
            emptyText="查無符合的資料"
            renderCell={(row, columnKey) => String(row[columnKey] ?? '')}
          />
        </ModalFrame>
      ) : null}

      {/* 投票結果 modal（modal/voteResultModal.jsp：title={name}-投票結果，7 欄）。 */}
      {resultModal !== null ? (
        <ModalFrame
          uiId={`${pageId}-result-modal`}
          open
          title={resultModal.title}
          size="lg"
          onClose={() => {
            setResultModal(null);
          }}
          footer={
            <Button
              uiId={`${pageId}-result-modal-footer-close`}
              variant="default"
              size="sm"
              type="button"
              onClick={() => {
                setResultModal(null);
              }}
            >
              關閉
            </Button>
          }
        >
          <Table
            uiId={`${pageId}-result-table-modal`}
            columns={[
              { key: 'category', header: '類別', align: 'center' },
              { key: 'deptName', header: '科部', align: 'center' },
              { key: 'accountName', header: '姓名', align: 'center' },
              { key: 'studentVotedCount', header: '總得票數', align: 'center' },
              { key: 'studentTotalVoteCount', header: '總可被投票數', align: 'center' },
              { key: 'studentVoteScore', header: '推薦指數', align: 'center' },
              { key: 'deptManagerRecommend', header: '單位主管推薦', align: 'center' },
            ]}
            rows={VOTE_RESULT_SAMPLE as readonly TableRow[]}
            rowKey={(row) => String(row['accountName'] ?? '')}
            emptyText="查無符合的資料"
            renderCell={(row, columnKey) => {
              if (columnKey === 'category') {
                return String(row['category'] ?? '');
              }
              return String(row[columnKey] ?? '');
            }}
          />
        </ModalFrame>
      ) : null}
    </div>
  );
}
